import { createContext, useContext, useCallback, useState, useEffect, useMemo, useRef, type ReactNode } from "react";

interface SearchFilters {
  query: string;
  categoryId: string;
  minPrice?: number;
  maxPrice?: number;
  location?: string;
  radius?: number; // km
  sortBy: "relevance" | "newest" | "price_low" | "price_high" | "distance";
}

interface RecentSearch {
  query: string;
  timestamp: number;
}

// Split contexts for granular subscriptions
interface SearchFiltersContextType {
  filters: SearchFilters;
  setFilters: (filters: Partial<SearchFilters>) => void;
  resetFilters: () => void;
}

interface RecentSearchesContextType {
  recentSearches: RecentSearch[];
  addRecentSearch: (query: string) => void;
  clearRecentSearches: () => void;
  removeRecentSearch: (query: string) => void;
}

const defaultFilters: SearchFilters = {
  query: "",
  categoryId: "",
  minPrice: undefined,
  maxPrice: undefined,
  location: undefined,
  radius: 25,
  sortBy: "relevance",
};

const STORAGE_KEY = "listo_search_filters";
const RECENT_SEARCHES_KEY = "listo_recent_searches";
const MAX_RECENT_SEARCHES = 10;

const SearchFiltersContext = createContext<SearchFiltersContextType | null>(null);
const RecentSearchesContext = createContext<RecentSearchesContextType | null>(null);

interface SearchProviderProps {
  children: ReactNode;
  persistFilters?: boolean;
}

// Debounced storage write to prevent excessive writes
function useDebouncedStorage<T>(key: string, value: T, delay: number = 300) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch {
        // Ignore storage errors
      }
    }, delay);
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [key, value, delay]);
}

export function SearchProvider({ children, persistFilters = true }: SearchProviderProps) {
  const [filters, setFiltersState] = useState<SearchFilters>(() => {
    if (persistFilters && typeof window !== "undefined") {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
          return { ...defaultFilters, ...JSON.parse(stored) };
        }
      } catch {
        // Ignore parse errors
      }
    }
    return defaultFilters;
  });

  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch {
        // Ignore parse errors
      }
    }
    return [];
  });

  // Debounced persistence for filters
  useDebouncedStorage(STORAGE_KEY, filters, 300);

  // Persist recent searches immediately (less frequent updates)
  useEffect(() => {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recentSearches));
  }, [recentSearches]);

  // Memoize filter actions
  const filtersContextValue = useMemo<SearchFiltersContextType>(() => ({
    filters,
    setFilters: (newFilters: Partial<SearchFilters>) => {
      setFiltersState((prev) => ({ ...prev, ...newFilters }));
    },
    resetFilters: () => {
      setFiltersState(defaultFilters);
    },
  }), [filters]);

  // Memoize recent searches actions
  const recentSearchesContextValue = useMemo<RecentSearchesContextType>(() => ({
    recentSearches,
    addRecentSearch: (query: string) => {
      if (!query.trim()) return;
      setRecentSearches((prev) => {
        const filtered = prev.filter((s) => s.query.toLowerCase() !== query.toLowerCase());
        const newSearch: RecentSearch = { query: query.trim(), timestamp: Date.now() };
        return [newSearch, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      });
    },
    clearRecentSearches: () => {
      setRecentSearches([]);
    },
    removeRecentSearch: (query: string) => {
      setRecentSearches((prev) => prev.filter((s) => s.query !== query));
    },
  }), [recentSearches]);

  return (
    <SearchFiltersContext.Provider value={filtersContextValue}>
      <RecentSearchesContext.Provider value={recentSearchesContextValue}>
        {children}
      </RecentSearchesContext.Provider>
    </SearchFiltersContext.Provider>
  );
}

// Granular hooks for better performance
export function useSearchFilters() {
  const context = useContext(SearchFiltersContext);
  if (!context) {
    throw new Error("useSearchFilters must be used within a SearchProvider");
  }
  return context;
}

export function useRecentSearches() {
  const context = useContext(RecentSearchesContext);
  if (!context) {
    throw new Error("useRecentSearches must be used within a SearchProvider");
  }
  return context;
}

// Selector hooks for individual filter values
export function useSearchQuery(): [string, (query: string) => void] {
  const { filters, setFilters } = useSearchFilters();
  const setQuery = useCallback((query: string) => setFilters({ query }), [setFilters]);
  return [filters.query, setQuery];
}

export function useSortBy(): [SearchFilters["sortBy"], (sortBy: SearchFilters["sortBy"]) => void] {
  const { filters, setFilters } = useSearchFilters();
  const setSortBy = useCallback((sortBy: SearchFilters["sortBy"]) => setFilters({ sortBy }), [setFilters]);
  return [filters.sortBy, setSortBy];
}

// Combined hook for backwards compatibility
export function useSearch() {
  const filtersContext = useSearchFilters();
  const recentContext = useRecentSearches();
  return { ...filtersContext, ...recentContext };
}

export type { SearchFilters, RecentSearch };
